<template>
	<div>
		<app-eye-doc-stepper step="4"></app-eye-doc-stepper>

		<v-card :loading="loading" tile>
			<v-card-title primary-title>
				<div class="title">Grading page</div>
			</v-card-title>
		</v-card>

		<v-card v-if="show" :style="{maxHeight: height+'px'}" flat tile>
			<v-banner sticky single-line tile>
				<span v-if="patient">{{ patient.id }} - {{ patient.gender }} - {{ patient.age }}</span>
				<div v-if="medicalLabels">
					<span v-for="(label, index) in medicalLabels" :key="label.id">
						{{ label.result }}
						<span v-if="index != medicalLabels.length-1">-</span>
					</span>
				</div>
				<div v-if="valid">{{ getReport }}</div>

				<template #actions>
					<v-tooltip v-for="(retina, index) in rightRetinas" :key="retina.id" bottom abolute>
						<template v-slot:activator="{ on }">
							<v-img
								:src="retina.path"
								height="50px"
								max-width="50px"
								style="cursor: pointer"
								v-on="on"
								@click="step = index"
							>
								<v-row v-if="retina.valid" no-gutters class="text-end align-end fill-height">
									<v-col>
										<v-icon color="primary">mdi-check-circle</v-icon>
									</v-col>
								</v-row>
							</v-img>
						</template>
						<span>{{ retina.label }}</span>
					</v-tooltip>

					<v-divider vertical></v-divider>

					<v-tooltip v-for="(retina, index) in leftRetinas" :key="retina.id" bottom abolute>
						<template v-slot:activator="{ on }">
							<v-img
								:src="retina.path"
								height="50px"
								max-width="50px"
								style="cursor: pointer"
								v-on="on"
								@click="step = index + rightRetinas.length"
							>
								<v-row v-if="retina.valid" no-gutters class="text-end align-end fill-height">
									<v-col>
										<v-icon color="primary">mdi-check-circle</v-icon>
									</v-col>
								</v-row>
							</v-img>
						</template>
						<span>{{ retina.label }}</span>
					</v-tooltip>

					<v-divider vertical></v-divider>

					<v-tooltip bottom abolute>
						<template v-slot:activator="{ on }">
							<v-icon
								:color="valid ? 'primary' : ''"
								size="50"
								:disabled="!valid"
								v-on="on"
								@click="step = retinas.length"
							>
								<template>mdi-share</template>
							</v-icon>
						</template>
						<span>Proceed to report</span>
					</v-tooltip>

					<v-row dense>
						<v-col cols="12" md="6">
							<v-slider
								v-model="zoom"
								hide-details
								min="3"
								max="16"
								thumb-label
								append-icon="mdi-magnify-plus"
								prepend-icon="mdi-magnify-minus"
								@click:append="zoom++;"
								@click:prepend="zoom--;"
							></v-slider>
						</v-col>
						<v-col cols="12" md="6">
							<v-slider
								v-model="contrast"
								hide-details
								min="0"
								max="1"
								step="0.1"
								append-icon="mdi-invert-colors-off"
								prepend-icon="mdi-invert-colors"
								@click:append="contrast += 0.1;"
								@click:prepend="contrast -= 0.1;"
							></v-slider>
						</v-col>
					</v-row>
				</template>
			</v-banner>

			<v-card-text
				v-show="step != retinas.length"
				class="pa-0"
				:style="{maxHeight: (height - 67 - 48)+'px'}"
			>
				<v-row style="max-height: inherit;" no-gutters>
					<v-col
						v-show="questionsDrawer"
						cols="2"
						style="max-height: inherit; overflow: auto"
						class="px-2"
					>
						<v-row no-gutters>
							<v-form v-model="valid">
								<template v-for="(retina, index) in retinas">
									<app-questionnaire
										v-show="step == index"
										:key="index.id"
										v-model="retina.answers"
										:questions="retina.questions"
										:appointment-id="appointmentId"
										:doc-id="docId"
										:related-result="retina.id"
										sectioned
									></app-questionnaire>
								</template>
								<v-btn class="text-none red--text" text block @click="questionsDrawer = false">
									<template>Hide questions</template>
								</v-btn>
							</v-form>

							<template v-for="(retina, index) in retinas">
								<!-- Checking values -->
								<v-form :key="index.id" v-model="retina.valid" style="display: none">
									<app-questionnaire
										v-show="step == index"
										v-model="retina.answers"
										:questions="retina.questions"
										:appointment-id="appointmentId"
										:doc-id="docId"
										:related-result="retina.id"
										sectioned
									></app-questionnaire>
								</v-form>
							</template>
						</v-row>
					</v-col>

					<v-col class="expand" style="overflow: auto" :style="{height: (height - 67 - 48)+'px'}">
						<v-card-text style="width: 100%; height: 100%;" class="pa-0">
							<div class="img-zoom-container" style="height: 100%">
								<template v-for="(retina, index) in retinas">
									<template v-if="step == index">
										<img id="image" :key="retina.id" :src="retina.path" width="300" height="200" />
									</template>
								</template>

								<div
									id="zoom-result"
									class="img-zoom-result"
									style="background-color: black;"
									:style="'filter: grayscale('+applyGrayScale+');'"
								>
									<v-tooltip v-if="!questionsDrawer" right>
										<template v-slot:activator="{ on }">
											<v-btn
												fab
												bottom
												left
												color="primary"
												absolute
												style="bottom: 16px;"
												@click.native.stop="questionsDrawer = true"
												v-on="on"
											>
												<v-icon>mdi-format-list-checks</v-icon>
											</v-btn>
										</template>
										<span>Show questions</span>
									</v-tooltip>
								</div>
							</div>
						</v-card-text>
					</v-col>
				</v-row>
			</v-card-text>

			<v-card-text v-if="step == retinas.length">
				<v-container>
					<v-row dense>
						<v-col cols="12" class="text-center">
							<v-textarea v-model="getReport" label="Report" disabled filled></v-textarea>
						</v-col>

						<v-col cols="12">
							<v-row class="text-center" justify="center" dense>
								<v-col class="shrink">
									<v-btn color="error" class="text-none" rounded outlined disabled>Cancel</v-btn>
								</v-col>
								<v-col class="shrink">
									<v-btn color="secondary" class="text-none" rounded outlined disabled>Return back to Data Entry</v-btn>
								</v-col>
								<v-col class="shrink">
									<v-btn color="secondary" class="text-none" rounded outlined disabled>Second Opinion</v-btn>
								</v-col>
								<v-col class="shrink">
									<v-btn color="secondary" class="text-none" rounded outlined @click="submit">Save</v-btn>
								</v-col>
								<v-col class="shrink">
									<v-btn color="primary" class="text-none" rounded @click="submit">Save and exit</v-btn>
								</v-col>
							</v-row>
						</v-col>
					</v-row>
				</v-container>
			</v-card-text>
		</v-card>
	</div>
</template>

<script>
const axios = require("axios");

export default {
	components: {
		"app-eye-doc-stepper": require("@/components/steppers/AppEyeDocStepper.vue")
			.default,
		"app-questionnaire": require("@/components/AppQuestionnaire.vue")
			.default
	},
	mixins: [
		require("@/mixins/backend-mixin").default,
		require("@/mixins/rules-mixin").default,
		require("@/mixins/router-mixin").default,
		require("@/mixins/utilities-mixin").default
	],
	data: () => ({
		zoom: 3,
		contrast: 0,
		step: null,
		height: null,

		patient: null,
		questions: [],
		valid: false,
		answers: [],
		appointmentId: null,
		docId: null,
		loading: true,
		retinas: [],
		medicalLabels: [],
		show: false,
		questionsDrawer: false,
		rightRetinas: [],
		leftRetinas: []
	}),
	computed: {
		getSize() {
			return this.zoom * 100 + "%";
		},
		applyGrayScale() {
			return this.contrast * 100 + "%";
		},
		getReport() {
			var min = Infinity;

			var answers = [];

			for (let retina of this.retinas) {
				answers.push(...retina.answers);
			}

			answers = answers.map(answer => {
				// remove duration
				var newAnswer = {
					appointmentId: answer.appointmentId,
					docId: answer.docId,
					result: answer.result,
					note: answer.note,
					relatedResult: answer.relatedResult
				};

				return newAnswer;
			});

			console.log(answers);

			for (let retina of this.retinas) {
				if (retina.answers) {
					console.log(retina.answers);
					for (var answer of retina.answers) {
						min = Math.min(min, answer.duration);
					}
				}
			}

			if (min == 1) {
				min = "1 week";
			} else if (min < 4) {
				min = min + " weeks";
			} else if (min == 4) {
				min = "1 month";
			} else {
				min = min / 4 + " months";
			}

			return "Refer to eye doctor within " + min;
		}
	},
	watch: {
		zoom() {
			imageZoom("image", "zoom-result", this.zoom);
		},
		step() {
			setTimeout(() => {
				imageZoom("image", "zoom-result", this.zoom);
			}, 5);
		},
		retinas(val) {
			this.rightRetinas = val.filter(
				retina => retina.label == "Right Retina"
			);
			this.leftRetinas = val.filter(
				retina => retina.label == "Left Retina"
			);
		}
	},
	async mounted() {
		this.appointmentId = this.$route.params.appointmentId;
		this.docId = await this.$store.state.userInfo.nationalId;
		await this.getPatientInfo();
		await this.getMedicalLabels();
		await this.getRetinas();
		// await this.getQuestions();

		this.step = 0;

		setTimeout(() => {
			this.setHeight();
			// Initiate zoom effect:
			imageZoom("image", "zoom-result", 1);
		}, 1000);

		this.show = true;
	},
	methods: {
		showImage() {
			this.zoom = 1;
			this.contrast = 0;
		},
		goToResult() {
			this.$router.push("/report/" + this.appointmentId);
		},
		setHeight() {
			this.height = window.innerHeight;
		},
		async getPatientInfo() {
			var id = this.$route.params.appointmentId;

			const url = this.BACKEND_URL + "/api/getAppointmentById/" + id;
			const thisVue = this;

			await axios
				.get(url)
				.then(response => {
					console.log(response);
					if (response.status == 200) {
						thisVue.patient = response.data.user;
						thisVue.patient.id = thisVue.patient.nationalId;
						thisVue.patient.name =
							thisVue.patient.FirstName +
							" " +
							thisVue.patient.FatherName +
							" " +
							thisVue.patient.FamilyName;
						thisVue.patient.age =
							thisVue.getAge(thisVue.patient.birth) + " Years";
					}
				})
				.catch(error => {
					console.error(error);
				});
		},
		async getMedicalLabels() {
			var id = this.$route.params.appointmentId;

			const url =
				this.BACKEND_URL +
				"/api/getDiagnosisResultForAppointment/" +
				id;
			const thisVue = this;

			await axios
				.get(url)
				.then(response => {
					console.log(response);
					if (response.status == 200) {
						thisVue.medicalLabels = response.data.data;
					}
				})
				.catch(error => {
					console.error(error);
				});
		},
		async getRetinas() {
			const thisVue = this;

			var rightRetinas = this.medicalLabels
				.filter(label => label.question == "Right retina")
				.map(retina => {
					retina.label = "Right Retina";
					return retina;
				});

			var leftRetinas = this.medicalLabels
				.filter(label => label.question == "Left retina")
				.map(retina => {
					retina.label = "Left Retina";
					return retina;
				});

			var retinas = [...rightRetinas, ...leftRetinas];

			var promises = await retinas.map(async function(retina) {
				console.log(retina.id);

				var url = thisVue.BACKEND_URL + "/api/getImage";

				let data = {
					imgName: retina.note
				};

				var r = {};

				await axios
					.post(url, data)
					.then(response => {
						console.log("retina");
						if (response.status == 200) {
							r = {
								id: retina.id,
								label: retina.label,
								path: thisVue.BACKEND_URL + response.data,
								answers: []
							};
						}
					})
					.catch(error => {
						console.error(error);
					});

				return r;
			});

			await Promise.all(promises).then(async completed => {
				console.log("completed");
				thisVue.retinas = await completed;
			});

			await this.getQuestions(thisVue.retinas);
		},
		async getQuestions(retinas) {
			const stage = 4;
			const url =
				this.BACKEND_URL + "/api/getStageDiagnosisType/" + stage;

			const thisVue = this;

			await axios
				.get(url)
				.then(response => {
					if (response.status == 200) {
						var questions = response.data.data;

						retinas.forEach(retina => {
							retina.questions = JSON.parse(
								JSON.stringify(questions)
							);
						});

						// thisVue.retinas = thisVue.retinas.map(retina => {
						// 	retina.questions = JSON.parse(JSON.stringify(questions));
						// 	retina.answers = [];
						// 	return retina;
						// });

						// refactor to remove this
						// thisVue.questions = questions;

						this.show = true;

						thisVue.loading = false;
					}
				})
				.catch(error => {
					console.error(error);
				});

			this.retinas = retinas;
		},
		submit() {
			var answers = [];

			for (var retina of this.retinas) {
				answers.push(...retina.answers);
			}

			answers = answers.map(answer => {
				// remove duration
				var newAnswer = {
					appointmentId: answer.appointmentId,
					docId: answer.docId,
					result: answer.result,
					note: answer.note,
					relatedResult: answer.relatedResult
				};

				return newAnswer;
			});

			this.submitAll(answers);
		},
		submitAll(answers) {
			const url = this.BACKEND_URL + "/api/storeArrayDignosisResult";

			const thisVue = this;

			var data = {
				array: answers
			};

			let config = {};

			axios
				.post(url, data, config)
				.then(response => {
					thisVue.$store.dispatch("triggerPushNotification", {
						message: response.data
					});

					thisVue.goToResult();
				})
				.catch(error => {
					thisVue.$store.dispatch("triggerPushNotification", {
						message: error.response.data
					});
				});
		}
	}
};

function imageZoom(imgID, resultID, zoom) {
	var img, lens, result, cx, cy;
	img = document.getElementById(imgID);
	if (!img) {
		return;
	}
	result = document.getElementById(resultID);
	/*create lens:*/
	lens = document.getElementById("lens");
	if (!lens) {
		lens = document.createElement("DIV");
		lens.id = "lens";
		lens.setAttribute("class", "img-zoom-lens");
	}
	/*insert lens:*/
	img.parentElement.append(lens, img);
	/*calculate the ratio between result DIV and lens:*/
	// cx = result.offsetWidth / lens.offsetWidth;
	// cy = result.offsetHeight / lens.offsetHeight;

	cx = zoom;
	cy = zoom;
	/*set background properties for the result DIV:*/
	result.style.backgroundImage = "url('" + img.src + "')";
	result.style.backgroundSize =
		img.width * cx + "px " + img.height * cy + "px";
	/*execute a function when someone moves the cursor over the image, or the lens:*/
	lens.addEventListener("mousemove", moveLens);
	img.addEventListener("mousemove", moveLens);
	/*and also for touch screens:*/
	lens.addEventListener("touchmove", moveLens);
	img.addEventListener("touchmove", moveLens);
	function moveLens(e) {
		var pos, x, y;
		/*prevent any other actions that may occur when moving over the image:*/
		e.preventDefault();
		/*get the cursor's x and y positions:*/
		pos = getCursorPos(e);
		/*calculate the position of the lens:*/
		x = pos.x - lens.offsetWidth / 2;
		y = pos.y - lens.offsetHeight / 2;
		/*prevent the lens from being positioned outside the image:*/
		if (x > img.width - lens.offsetWidth) {
			x = img.width - lens.offsetWidth;
		}
		if (x < 0) {
			x = 0;
		}
		if (y > img.height - lens.offsetHeight) {
			y = img.height - lens.offsetHeight;
		}
		if (y < 0) {
			y = 0;
		}
		/*set the position of the lens:*/
		lens.style.left = x + "px";
		lens.style.top = y + "px";
		/*display what the lens "sees":*/
		result.style.backgroundPosition = "-" + x * cx + "px -" + y * cy + "px";
	}
	function getCursorPos(e) {
		var a,
			x = 0,
			y = 0;
		e = e || window.event;
		/*get the x and y positions of the image:*/
		a = img.getBoundingClientRect();
		/*calculate the cursor's x and y coordinates, relative to the image:*/
		x = e.pageX - a.left;
		y = e.pageY - a.top;
		/*consider any page scrolling:*/
		x = x - window.pageXOffset;
		y = y - window.pageYOffset;
		return { x: x, y: y };
	}
}
</script>

<style>
.v-input--selection-controls:not(.v-input--hide-details) .v-input__slot,
.v-input--radio-group--column .v-radio:not(:last-child):not(:only-child) {
	margin-bottom: 0;
}
.v-input--checkbox,
.v-input--radio-group {
	margin-top: 0;
	margin-bottom: 0;
}

.img-zoom-container {
	box-sizing: border-box;
	position: relative;
}

.img-zoom-lens {
	box-sizing: border-box;
	position: fixed;
	/*set the size of the lens:*/
	width: 40px;
	height: 40px;
}

.img-zoom-result {
	box-sizing: border-box;
	border: 1px solid #d4d4d4;
	/*set the size of the result div:*/
	height: 100%;
	width: 100%;
}

#image {
	border: 1px solid #d4d4d4;
	position: fixed;
	bottom: 0;
	right: 0;
}
</style>